import { Typography, Link as MUILink } from '@material-ui/core';
import { BannerWidgetData, useBanner } from './bannerContext';
import BannerWidget from './bannerWidget';
import React, { useState, useEffect } from 'react';
import { useUser, GroupMemberships } from '../../../context/userContext';
import { Progress } from '@backstage/core-components';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Alert } from '@material-ui/lab';

type UserContext = {
  kind: string;
  metadata: UserMetaData;
  relations: UserRelation[];
  spec: SpecData;
};

type SpecData = {
  type: string;
};

type UserMetaData = {
  name: string;
  namespace: string;
};

type UserRelation = {
  target: RelationTarget[];
  targetRef: string;
  type: string;
};

type RelationTarget = {
  kind: string;
  name: string;
  namespace: string;
};

export function squadNameFormat(squadName: string) {
  if (squadName.includes('squad')) return squadName;
  return `${squadName}-squad`;
}

const TechdocsOnboardingBanner = () => {
  const config = useApi(configApiRef);
  const baseURLforWidget = config.getOptionalString('backend.baseUrl');
  const { fetch: backstageFetch } = useApi(fetchApiRef);

  const [groupMemberships, setGroupMemberships] = useState<GroupMemberships[]>(
    [],
  );
  const [loadingComponents, setLoadingComponents] = useState(true);
  const [loadingUser, setLoadingUser] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [docComponents, setDocComponents] = useState<number>(-1);

  const { getUser } = useUser();
  useEffect(() => {
    (async () => {
      const userData = await getUser();
      setGroupMemberships(userData.groupMemberships);
      setLoadingUser(false);
    })();
  }, [getUser]);

  useEffect(() => {
    (async () => {
      await Promise.all(
        groupMemberships.map(async obj => {
          const squadName = squadNameFormat(obj.squad);
          return await backstageFetch(
            `${baseURLforWidget}/api/catalog/entities?filter=kind=Component,kind=API,kind=System,relations.ownedBy=group%3Adefault%2F${squadName}&fields=kind,metadata.name,metadata.namespace,spec.type,relations`,
          )
            .then(res => {
              if (!res.ok) throw new Error(`Catalog request failed: ${res.status}`);
              return res.json();
            })
            .then(json => {
              const returnArray: UserContext[] = json.flat();

              if (returnArray.length > 0) {
                setDocComponents(
                  returnArray.filter(
                    entity =>
                      entity.spec !== undefined &&
                      entity.spec.type === 'documentation',
                  ).length,
                );
              }
            })
            .catch(err => {
              setCatalogError(err);
            });
        }),
      );
      setLoadingComponents(false);
    })();
  }, [groupMemberships, baseURLforWidget, backstageFetch]);

  const techdocsBannerInformation: BannerWidgetData = {
    BannerData: {
      // when adding your new announcement, you wil need to pass in a string UUID to the component as well. This can be any string you want as long as its unique in some form.
      // This UUID will be stored in the bannerContext object for the user.
      UUID: 'techdocsWarning',

      // When adding links to your new announcement, use the updateBanner() on the onClick prop so that when users click on the link, it updates the context so banner isn't shown again.
      announcement: (
        <Typography>
          <span
            style={{ fontWeight: 'bold', fontSize: '14px', color: 'black' }}
          >
            Looks like your team hasn't onboarded their documentation to
            TechDocs.
          </span>{' '}
          <span style={{ fontSize: '14px', color: 'black' }}>
            Read our
            <MUILink
              style={{ color: 'blue' }}
              target="_blank"
              href="https://devportal.us.acuitynext.io/docs/default/component/zephyr-techdocs/product-documentation/user/end-user/techdocs/"
            >
              {' '}
              guide{' '}
            </MUILink>
            to get started!
          </span>
        </Typography>
      ),

      // color codes ["success" <- green, "warning" <- yellow]
      color: 'warning',
    },
  };

  const techdocsErrorBannerInformation: BannerWidgetData = {
    BannerData: {
      // when adding your new announcement, you wil need to pass in a string UUID to the component as well. This can be any string you want as long as its unique in some form.
      // This UUID will be stored in the bannerContext object for the user.
      UUID: 'techdocsWarning',

      // When adding links to your new announcement, use the updateBanner() on the onClick prop so that when users click on the link, it updates the context so banner isn't shown again.
      announcement: (
        <Typography>
          <span
            style={{ fontWeight: 'bold', fontSize: '14px', color: 'black' }}
          >
            Looks like there was an error rendering this banner. Reload the page
            to try again.
          </span>{' '}
        </Typography>
      ),

      // color codes ["success" <- green, "warning" <- yellow]
      color: 'warning',
    },
  };

  const renderBanner = () => {
    if (loadingComponents || loadingUser) return <Progress />;
    else if (catalogError)
      return (
        <BannerWidget
          announcementUUID={techdocsErrorBannerInformation.BannerData.UUID}
          announcementText={
            techdocsErrorBannerInformation.BannerData.announcement
          }
          bannerSeverity={techdocsErrorBannerInformation.BannerData.color}
        />
      );
    else if (docComponents === 0)
      return (
        <BannerWidget
          announcementUUID={techdocsBannerInformation.BannerData.UUID}
          announcementText={techdocsBannerInformation.BannerData.announcement}
          bannerSeverity={techdocsBannerInformation.BannerData.color}
        />
      );
    return null;
  };

  return <>{renderBanner()}</>;
};

export default TechdocsOnboardingBanner;
